import { ReportContentItem } from '../models/run.model';

/** Columns that identify which report content item a row belongs to, shown when rows span several. */
export const REPORT_ITEM_COLUMNS = {
  assay: 'Assay',
  kitLot: 'Kit Lot',
  instrument: 'Instrument'
};

export const REPORT_ITEM_COLUMN_KEYS = Object.keys(REPORT_ITEM_COLUMNS);

/** Internal QC bead regions sort after the assay regions. */
const INTERNAL_QC_BEAD_REGIONS = new Set<number>([34, 98]);

/** `runId` is the instrument ID in this app. */
export function reportItemColumnValues(item: ReportContentItem | undefined): { assay: string; kitLot: string; instrument: string } {
  return {
    assay: item?.assay ?? '',
    kitLot: item?.lot ?? '',
    instrument: item?.runId ?? ''
  };
}

/** Prepends the report item columns to a column map, keeping the given columns' order. */
export function withReportItemColumns<T extends Record<string, string>>(columns: T): Record<string, string> {
  return { ...REPORT_ITEM_COLUMNS, ...columns };
}

/** Orders items by bead region, reference kit lot first, kit lot, run, then assay. */
export function sortReportContentItems(items: ReportContentItem[], refKitLot = ''): ReportContentItem[] {
  const refLot = refKitLot.trim();
  return [...items].sort((a, b) => {
    const regionCompare = compareBeadRegions(a.region, b.region);
    if (regionCompare !== 0) {
      return regionCompare;
    }

    if (refLot) {
      const aIsRef = (a.lot ?? '') === refLot;
      const bIsRef = (b.lot ?? '') === refLot;
      if (aIsRef !== bIsRef) {
        return aIsRef ? -1 : 1;
      }
    }

    const kitCompare = (a.lot ?? '').localeCompare(b.lot ?? '');
    if (kitCompare !== 0) {
      return kitCompare;
    }

    const runCompare = (a.runId ?? '').localeCompare(b.runId ?? '');
    if (runCompare !== 0) {
      return runCompare;
    }

    return (a.assay ?? '').localeCompare(b.assay ?? '');
  });
}

function compareBeadRegions(left: string | number | undefined, right: string | number | undefined): number {
  const leftRegion = parseRegionValue(left);
  const rightRegion = parseRegionValue(right);
  const leftInternal = INTERNAL_QC_BEAD_REGIONS.has(leftRegion);
  const rightInternal = INTERNAL_QC_BEAD_REGIONS.has(rightRegion);
  if (leftInternal !== rightInternal) {
    return leftInternal ? 1 : -1;
  }
  return leftRegion - rightRegion;
}

function parseRegionValue(region: string | number | undefined): number {
  if (typeof region === 'number') {
    return Number.isNaN(region) ? 0 : region;
  }
  const parsed = Number(region);
  return Number.isNaN(parsed) ? 0 : parsed;
}
